import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { adminGuard, emailVerifiedGuard } from 'auth';
import { AppointmentsPageComponent, MyCardsPageComponent, UserTreePageComponent } from 'credo';
import { UsersPageComponent } from './admin/containers/users-page/users-page.component';
import { XshopAdminPageComponent } from './admin/containers/xshop-admin-page/xshop-admin-page.component';
import { BetaActivationPageComponent } from './containers/pages/beta-activation-page/beta-activation-page.component';
import { BookingsPageComponent } from './containers/pages/bookings-page/bookings-page.component';
import { DeleteAccountPageComponent } from './containers/pages/delete-account-page/delete-account-page.component';
import { EarningsStatisticsPageComponent } from './containers/pages/earnings-statistics-page/earnings-statistics-page.component';
import { FunnelCheckEmailPageComponent } from './containers/pages/funnel-check-email-page/funnel-check-email-page.component';
import { FunnelDirectCheckoutPageComponent } from './containers/pages/funnel-direct-checkout-page/funnel-direct-checkout-page.component';
import { FunnelJoinStartPageComponent } from './containers/pages/funnel-join-start-page/funnel-join-start-page.component';
import { FunnelCheckoutPageComponent } from './containers/pages/funnel-checkout-page/funnel-checkout-page.component';
import { FunnelPaymentPageComponent } from './containers/pages/funnel-payment-page/funnel-payment-page.component';
import { FunnelQuizPageComponent } from './containers/pages/funnel-quiz-page/funnel-quiz-page.component';
import { FunnelQuizStartPageComponent } from './containers/pages/funnel-quiz-start-page/funnel-quiz-start-page.component';
import { FunnelResultPageComponent } from './containers/pages/funnel-result-page/funnel-result-page.component';
import { FunnelSuccessPageComponent } from './containers/pages/funnel-success-page/funnel-success-page.component';
import { LandingPageComponent } from './containers/pages/landing-page/landing-page.component';
import { MainPageComponent } from './containers/pages/main-page/main-page.component';
import { WithdrawalPageComponent } from './containers/pages/withdrawal-page/withdrawal-page.component';
import { UserPayoutRequestPageComponent } from './containers/pages/user-payout-request-page/user-payout-request-page.component';
import { UserPayoutsPageComponent } from './containers/pages/user-payouts-page/user-payouts-page.component';
import { UserStatisticsPageComponent } from './containers/pages/user-statistics-page/user-statistics-page.component';

import { routeBuilder } from 'credo';

export const routes = routeBuilder()
  .main(MainPageComponent)
  .withMaintenance()
  .withLegalAcknowledgement()
  .withCommunity()
  // "Register" on the login page leads into the Platinum direct checkout.
  .withRegisterUrl('/r')
  .root(
    // Public marketing landing page at the bare root URL (overrides the
    // default '' -> /en redirect appended by the route builder).
    { path: '', component: LandingPageComponent, pathMatch: 'full', data: { animation: 'landing' } },
    { path: 'withdrawal', component: WithdrawalPageComponent, data: { animation: 'withdrawal' } },
    {
      path: 'enter-b-e-t-a-test',
      redirectTo: () => {
        const lang = localStorage.getItem('language') || 'en';
        return `/${lang}/enter-b-e-t-a-test`;
      }
    },
    // FAC3LESS funnel — short marketing URLs (no :lang prefix) redirect into the lang-scoped page.
    // The trailing token/id is preserved by closing over the URL via the redirectTo callback.
    //
    // Both return a UrlTree, not a string: an absolute STRING redirect rebuilds the
    // query from the redirect target only, so `/r/123?plan=platinum&ref=…` would
    // arrive as `/en/r/123` and the landing page's hand-over would be lost. The
    // redirect function runs in an injection context, so inject() works.
    {
      path: 'r',
      // pathMatch: 'full' so this only matches the bare /r — without it Angular's
      // default 'prefix' match swallows /r/:sponsorId too and drops the sponsor id.
      pathMatch: 'full',
      redirectTo: ({ queryParams, fragment }) => {
        const lang = localStorage.getItem('language') || 'en';
        return inject(Router).createUrlTree(['/', lang, 'r'], { queryParams, fragment: fragment ?? undefined });
      }
    },
    {
      path: 'r/:sponsorId',
      redirectTo: ({ params, queryParams, fragment }) => {
        const lang = localStorage.getItem('language') || 'en';
        return inject(Router).createUrlTree(['/', lang, 'r', params['sponsorId']],
                                            { queryParams, fragment: fragment ?? undefined });
      }
    }
  )
  .lang.public(
    // FAC3LESS funnel pages (public — no auth required).
    // /r = Platinum direct checkout (customer decision 2026-10-09: buy without prior
    // registration; the Card Finder quiz is no longer on the standard path).
    { path: 'r',                 component: FunnelDirectCheckoutPageComponent, data: { animation: 'funnel-direct-checkout' } },
    { path: 'r/:sponsorId',      component: FunnelDirectCheckoutPageComponent, data: { animation: 'funnel-direct-checkout' } },
    // Landing of the direct checkout's account / sign-in mail (token -> password -> wizard).
    { path: 'join/start',        component: FunnelJoinStartPageComponent,  data: { animation: 'funnel-join-start' } },
    // Retired Card Finder quiz: no longer linked; kept routed so quiz links in old
    // mails still work (its "start over" fallbacks now land on the direct checkout).
    { path: 'quiz/check-email',  component: FunnelCheckEmailPageComponent, data: { animation: 'funnel-check-email' } },
    { path: 'quiz/start',        component: FunnelQuizStartPageComponent,  data: { animation: 'funnel-quiz-start' } },
    { path: 'quiz',              component: FunnelQuizPageComponent,       data: { animation: 'funnel-quiz' } },
    { path: 'quiz/result',       component: FunnelResultPageComponent,     data: { animation: 'funnel-result' } },
    { path: 'quiz/checkout',     component: FunnelCheckoutPageComponent,   data: { animation: 'funnel-checkout' } },
    { path: 'quiz/payment',      component: FunnelPaymentPageComponent,    data: { animation: 'funnel-payment' } },
    { path: 'quiz/success',      component: FunnelSuccessPageComponent,    data: { animation: 'funnel-success' } }
  )
  // .replace('main', {
  //   path: 'main',
  //   component: MainPageComponent,
  //   canActivate: [emailVerifiedGuard],
  //   data: { title: 'Dashboard', animation: 'main' }
  // })
  .protected(
    {
      path: 'users',
      component: UsersPageComponent,
      data: { title: 'Users', animation: 'users' }
    },
    {
      path: 'user-statistics',
      component: UserStatisticsPageComponent,
      data: { title: 'User Statistics', animation: 'user-statistics' }
    },
    {
      path: 'earnings-statistics',
      component: EarningsStatisticsPageComponent,
      data: { title: 'Earnings Statistics', animation: 'earnings-statistics' }
    },
    {
      path: 'user-tree',
      component: UserTreePageComponent,
      data: { title: 'User Tree', animation: 'user-tree' }
    },
    {
      path: 'user-tree/:userId',
      component: UserTreePageComponent,
      data: { title: 'User Tree', animation: 'user-tree' }
    },
    {
      path: 'bookings',
      component: BookingsPageComponent,
      data: { title: 'Bookings', animation: 'bookings' }
    },
    {
      path: 'my-payouts',
      component: UserPayoutsPageComponent,
      data: { title: 'My Payouts', animation: 'user-payouts' }
    },
    {
      path: 'my-payouts/request',
      component: UserPayoutRequestPageComponent,
      data: { title: 'Request Payout', animation: 'user-payout-request' }
    },
    {
      path: 'mycards',
      component: MyCardsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'My Cards', animation: 'mycards' }
    },
    // Appointments — the central calendar (Phase 1). Which appointments a member
    // sees on this board is decided by the backend (calendars × audiences).
    {
      path: 'appointments',
      component: AppointmentsPageComponent,
      canActivate: [emailVerifiedGuard],
      data: { title: 'Appointments', animation: 'appointments' }
    },
    {
      path: 'delete-account',
      component: DeleteAccountPageComponent,
      data: { title: 'Delete Account', animation: 'delete-account' }
    },
    {
      path: 'enter-b-e-t-a-test',
      component: BetaActivationPageComponent,
      data: { title: 'Beta Program', animation: 'beta-activation' }
    },
    {
      path: 'xshop-admin',
      component: XshopAdminPageComponent,
      data: { title: 'XShop Admin', animation: 'xshop-admin' }
    },
    {
      path: 'admin',
      canMatch: [adminGuard],
      canActivate: [adminGuard],
      loadChildren: () =>
        import('./admin/admin.routes')
          .then(routes => routes.ADMIN_ROUTES)
    }
  )
  .build();

  